/**
 * Página de gerenciamento de orçamentos por categoria
 * 
 * Esta página permite visualizar, criar e editar orçamentos mensais
 * por categoria, fornecendo controle sobre gastos planejados vs reais.
 * Inclui filtros por período e visualização de progresso com indicadores
 * visuais para orçamentos estourados.
 * 
 * @author Amazon Q Developer
 * @since 1.0.0
 */

import { LitElement, html } from 'lit'
import '../components/modal-orcamento.js'
import '../components/premium-guard.js'
import '../components/seletor-periodo.js'
import { orcamentoCacheService } from '../core/services/orcamento-cache.service.js'
import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'

/**
 * Componente principal da página de orçamentos
 * 
 * Gerencia visualização e edição de orçamentos mensais por categoria,
 * com filtros por período e indicadores visuais de progresso.
 */
class Orcamento extends ProtectedPageMixin(LitElement) {
  static properties = {
    selectedPeriod: { type: String },
    budgets: { type: Array },
    filteredBudgets: { type: Array },
    loading: { type: Boolean },
    authError: { type: String },
    _openMenuId: { type: String, state: true },
    _budgetToDelete: { type: Object, state: true },
    _deleteError: { type: String, state: true }
  }

  /**
   * Mantém os estilos do Bulma CSS framework
   * 
   * @returns {HTMLElement} O próprio elemento para manter estilos globais
   */
  createRenderRoot() {
    return this // manter estilos do Bulma
  }

  /**
   * Inicializa o componente da página de orçamentos
   * 
   * Define período atual como padrão e configura listeners
   * para atualizações automáticas de orçamentos e categorias.
   */
  constructor() {
    super()
    const agora = new Date()
    /** @type {number} Mês atual para inicialização do seletor-periodo (0-11) */
    this._mesSeletor = agora.getMonth()
    /** @type {number} Ano atual para inicialização do seletor-periodo (4 dígitos) */
    this._anoSeletor = agora.getFullYear()
    this.selectedPeriod = this._getCurrentPeriod()
    this.budgets = []
    this.filteredBudgets = []
    this.loading = false
    /** @type {string|null} Mensagem de erro de autenticação (timeout ou falha) */
    this.authError = null
    /** @type {string|null} ID do orçamento com menu dropdown aberto */
    this._openMenuId = null
    /** @type {Object|null} Orçamento selecionado para exclusão (exibe modal de confirmação) */
    this._budgetToDelete = null
    /** @type {string|null} Mensagem de erro caso o soft-delete falhe */
    this._deleteError = null
    
    // Bind do handler para fechar menu ao clicar fora
    this._handleDocumentClick = this._handleDocumentClick.bind(this)
    
    // Bind dos handlers de cache para permitir remoção no disconnectedCallback
    this._handleOrcamentosCacheUpdated = this._handleOrcamentosCacheUpdated.bind(this)
    this._handleCategoriasCacheUpdated = this._handleCategoriasCacheUpdated.bind(this)
    
    // Listeners para atualizações automáticas (reatividade a eventos de cache)
    window.addEventListener('orcamentos-cache-updated', this._handleOrcamentosCacheUpdated)
    window.addEventListener('categorias-cache-updated', this._handleCategoriasCacheUpdated)
  }

  /**
   * Formata valores monetários para exibição em Real Brasileiro
   * 
   * Utiliza Intl.NumberFormat para garantir formatação consistente
   * independente do locale do navegador do usuário.
   * 
   * @param {number} amount - Valor numérico a ser formatado
   * @returns {string} Valor formatado como moeda brasileira (ex: "R$ 1.234,56")
   * @private
   */
  _formatCurrency(amount) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  /**
   * Retorna classe CSS para cor da barra de progresso
   * 
   * Classifica visualmente o progresso do orçamento conforme faixas:
   * - Acima de 100%: danger (vermelho) — orçamento estourado
   * - Entre 80% e 100%: warning (amarelo) — atenção
   * - Até 80%: primary (azul) — dentro do esperado
   * 
   * @param {number} percentual - Percentual utilizado do orçamento
   * @returns {string} Classe CSS Bulma para coloração da barra
   * @private
   */
  _getProgressColor(percentual) {
    if (percentual > 100) return 'has-background-danger'
    if (percentual > 80) return 'has-background-warning'
    return 'has-background-primary'
  }

  /**
   * Retorna classe CSS para cor do texto de valor disponível
   * 
   * Quando o percentual ultrapassa 100%, o texto do valor disponível
   * deve ser exibido em vermelho para alertar o usuário.
   * 
   * @param {number} percentual - Percentual utilizado do orçamento
   * @returns {string} Classe CSS Bulma para coloração do texto (vazio se normal)
   * @private
   */
  _getTextColor(percentual) {
    if (percentual > 100) return 'has-text-danger'
    return ''
  }

  /**
   * Calcula o valor disponível de um orçamento
   * 
   * O valor disponível é a diferença entre o valor orçado e o valor gasto.
   * Pode ser negativo quando o gasto ultrapassa o orçamento.
   * 
   * @param {Object} budget - Objeto de orçamento com valorOrcado e valorGasto
   * @returns {number} Valor disponível (orçado - gasto)
   * @private
   */
  _calcularDisponivel(budget) {
    return (budget.valorOrcado || budget.valorLimite || 0) - (budget.valorGasto || 0)
  }

  /**
   * Calcula o percentual utilizado de um orçamento
   * 
   * O percentual é arredondado para inteiro para exibição.
   * Se o valor orçado for zero, retorna 0 para evitar divisão por zero.
   * 
   * @param {Object} budget - Objeto de orçamento com valorOrcado e valorGasto
   * @returns {number} Percentual utilizado arredondado para inteiro
   * @private
   */
  _calcularPercentual(budget) {
    const orcado = budget.valorOrcado || budget.valorLimite || 0
    if (orcado === 0) return 0
    return Math.round(((budget.valorGasto || 0) / orcado) * 100)
  }

  /**
   * Renderiza o conteúdo financeiro do card de orçamento
   * 
   * Exibe informações de Orçado, Gasto, Disponível, barra de progresso
   * e percentual. Quando valorOrcado é zero, exibe mensagem informativa
   * sem barra de progresso.
   * 
   * @param {Object} budget - Objeto de orçamento completo
   * @returns {TemplateResult} Template Lit com o conteúdo do card
   * @private
   */
  _renderCardContent(budget) {
    const valorOrcado = budget.valorOrcado || budget.valorLimite || 0
    const valorGasto = budget.valorGasto || 0
    const disponivel = this._calcularDisponivel(budget)
    const percentual = this._calcularPercentual(budget)

    // Caso especial: orçamento não definido (valor zero)
    if (valorOrcado === 0) {
      return html`
        <div class="content" data-cy="budget-content">
          <div class="mb-2">
            <span class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">Orçado</span>
            <p class="is-size-6 has-text-weight-semibold" data-cy="budget-orcado" style="color: var(--text-primary, #F4F7FF);">${this._formatCurrency(valorOrcado)}</p>
          </div>
          <div class="mb-2">
            <span class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">Gasto</span>
            <p class="is-size-6 has-text-weight-semibold" data-cy="budget-gasto" style="color: var(--text-primary, #F4F7FF);">${this._formatCurrency(valorGasto)}</p>
          </div>
          <p class="is-size-7" data-cy="budget-sem-orcamento" style="color: var(--text-disabled, #6E7A96);">Nenhum orçamento definido</p>
        </div>
      `
    }

    return html`
      <div class="content" data-cy="budget-content">
        <div class="mb-2">
          <span class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">Orçado</span>
          <p class="is-size-6 has-text-weight-semibold" data-cy="budget-orcado" style="color: var(--text-primary, #F4F7FF);">${this._formatCurrency(valorOrcado)}</p>
        </div>
        <div class="mb-2">
          <span class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">Gasto</span>
          <p class="is-size-6 has-text-weight-semibold" data-cy="budget-gasto" style="color: var(--text-primary, #F4F7FF);">${this._formatCurrency(valorGasto)}</p>
        </div>
        <div class="mb-3">
          <span class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">Disponível</span>
          <p class="is-size-6 has-text-weight-semibold ${this._getTextColor(percentual)}" data-cy="budget-disponivel">${this._formatCurrency(disponivel)}</p>
        </div>
        
        <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
          <div class="progress-container" style="flex: 1; position: relative; height: 8px; background-color: var(--bg-surface-secondary, #1D2740); border-radius: var(--radius-badge, 999px); overflow: hidden;">
            <div 
              class="${this._getProgressColor(percentual)}"
              style="height: 100%; width: ${Math.min(percentual, 100)}%; transition: width 0.3s ease; border-radius: var(--radius-badge, 999px);"
              data-cy="budget-progress"
            ></div>
          </div>
          <span class="is-size-7 has-text-weight-semibold ${this._getTextColor(percentual)}" data-cy="budget-percentage" style="min-width: 2.5rem; text-align: right;">${percentual}%</span>
        </div>
      </div>
    `
  }

  /**
   * Manipula o evento periodo-changed emitido pelo componente seletor-periodo
   * 
   * Converte o mês (0-11) e ano do evento para o formato YYYY-MM utilizado
   * pelo orcamentoCacheService e recarrega os orçamentos do novo período.
   * 
   * @param {CustomEvent} e - Evento com detail: { mes: number (0-11), ano: number }
   * @private
   */
  async _onPeriodoChanged(e) {
    const { mes, ano } = e.detail
    // Converte mês (0-11) para string MM (01-12) e ano para string AAAA
    const mesStr = String(mes + 1).padStart(2, '0')
    const anoStr = String(ano)
    this.selectedPeriod = `${anoStr}-${mesStr}`
    await this._applyPeriodFilter()
    this.requestUpdate()
  }

  /**
   * Obtém o período atual no formato YYYY-MM
   * 
   * @returns {string} Período atual (ex: "2025-01")
   * @private
   */
  _getCurrentPeriod() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
  }

  /**
   * Formata o período selecionado para exibição na mensagem de estado vazio
   * 
   * Converte o formato YYYY-MM para "NomeMes YYYY" para exibir na UI.
   * 
   * @param {string} period - Período no formato "YYYY-MM"
   * @returns {string} Período formatado para exibição (ex: "Janeiro 2025")
   * @private
   */
  _formatPeriodDisplay(period) {
    if (!period) return ''
    const [year, month] = period.split('-')
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ]
    const monthIndex = parseInt(month) - 1
    return `${monthNames[monthIndex]} ${year}`
  }

  async _applyPeriodFilter() {
    try {
      // Garantir que sempre tenha um período selecionado
      if (!this.selectedPeriod) {
        this.selectedPeriod = this._getCurrentPeriod()
      }
      
      const [ano, mes] = this.selectedPeriod.split('-')
      
      // Buscar orçamentos do período (cache inteligente)
      this.budgets = await orcamentoCacheService.listarPorPeriodo(mes, ano)      
      this.filteredBudgets = [...this.budgets]
      
    } catch (error) {
      console.error('❌ Erro ao aplicar filtro de período:', error)
      this.budgets = []
      this.filteredBudgets = []
    }
  }

  /**
   * Handler para o evento `orcamentos-cache-updated` disparado pelo orcamentoCacheService
   * 
   * Recarrega a listagem de orçamentos do período atual a partir do cache local,
   * garantindo que orçamentos criados, editados ou excluídos reflitam
   * imediatamente na UI. O evento indica que o cache local já foi atualizado
   * pelo serviço emissor — basta recarregar dele sem invalidar.
   * 
   * @private
   */
  async _handleOrcamentosCacheUpdated() {
    await this._loadOrcamentos()
    this.requestUpdate()
  }

  /**
   * Handler para o evento `categorias-cache-updated` disparado pelo categoriaCacheService
   * 
   * Recarrega a listagem de orçamentos para refletir novas categorias
   * que podem ter gerado orçamentos automáticos (valorOrcado=0) pelo
   * orcamentoCacheService._handleCategoriaCreated().
   * Chama requestUpdate() como garantia extra de re-renderização pelo Lit.
   * 
   * @private
   */
  async _handleCategoriasCacheUpdated() {
    await this._loadOrcamentos()
    this.requestUpdate()
  }

  /**
   * Handler para cliques no documento — fecha o menu dropdown se clicado fora
   * 
   * Verifica se o clique foi dentro de um dropdown. Se não foi,
   * fecha qualquer menu aberto resetando `_openMenuId`.
   * 
   * @param {MouseEvent} event - Evento de clique no documento
   * @private
   */
  _handleDocumentClick(event) {
    // Verifica se o clique foi dentro de um elemento dropdown
    const dropdown = event.target.closest('.dropdown')
    if (!dropdown) {
      this._openMenuId = null
    }
  }

  /**
   * Alterna a visibilidade do menu dropdown de um card
   * 
   * Se o menu do card já está aberto, fecha-o. Caso contrário,
   * fecha qualquer outro menu aberto e abre o do card clicado.
   * 
   * @param {string} budgetId - ID do orçamento cujo menu será alternado
   * @param {Event} event - Evento de clique para interromper propagação
   * @private
   */
  _toggleMenu(budgetId, event) {
    event.stopPropagation()
    this._openMenuId = this._openMenuId === budgetId ? null : budgetId
  }

  /**
   * Inicia o processo de confirmação de exclusão de um orçamento
   * 
   * Fecha o menu dropdown e armazena o orçamento em `_budgetToDelete`
   * para exibir o modal de confirmação de exclusão. Limpa qualquer
   * erro anterior de tentativas falhas.
   * 
   * @param {Object} budget - Objeto do orçamento a ser excluído
   * @private
   */
  _confirmDelete(budget) {
    this._openMenuId = null
    this._deleteError = null
    this._budgetToDelete = budget
  }

  /**
   * Cancela a exclusão e fecha o diálogo de confirmação
   * 
   * Reseta o estado de exclusão sem executar nenhuma ação,
   * mantendo o card do orçamento na listagem.
   * 
   * @private
   */
  _cancelDelete() {
    this._budgetToDelete = null
    this._deleteError = null
  }

  /**
   * Executa o soft-delete do orçamento via orcamentoCacheService
   * 
   * Em caso de sucesso, o card é removido automaticamente da listagem
   * pelo evento `orcamentos-cache-updated` já configurado no constructor.
   * Em caso de falha, mantém o card na listagem e exibe mensagem de erro
   * dentro do diálogo de confirmação.
   * 
   * @private
   */
  async _executeDelete() {
    if (!this._budgetToDelete) return

    try {
      await orcamentoCacheService.softDelete(this._budgetToDelete.id)
      // Sucesso: fecha o modal — a listagem será atualizada pelo evento orcamentos-cache-updated
      this._budgetToDelete = null
      this._deleteError = null
    } catch (error) {
      // Falha: mantém o card e exibe mensagem de erro no diálogo
      console.error('Erro ao excluir orçamento:', error)
      this._deleteError = 'Não foi possível excluir o orçamento. Tente novamente.'
    }
  }

  _editBudget(budgetId) {
    this._openMenuId = null
    const budget = this.budgets.find(b => b.id === budgetId)
    if (budget) {
      const modal = this.renderRoot.querySelector('modal-orcamento')
      modal.show(budget)
    }
  }

  /**
   * Abre o modal de orçamento em modo de criação
   * 
   * Ao clicar no botão "Novo Orçamento", abre o Modal_Orcamento
   * sem argumentos para iniciar com campos vazios e
   * o select de categoria carregado com categorias disponíveis.
   * 
   * @private
   */
  _createBudget() {
    const modal = this.renderRoot.querySelector('modal-orcamento')
    modal.show()
  }

  /**
   * Carrega orçamentos do cache
   * 
   * Busca orçamentos do período atual por padrão do cache inteligente,
   * garantindo que sempre inicie com o mês corrente selecionado.
   * 
   * @private
   */
  async _loadOrcamentos() {
    try {
      // Garantir que o período atual esteja selecionado
      if (!this.selectedPeriod) {
        this.selectedPeriod = this._getCurrentPeriod()
      }
      
      await this._applyPeriodFilter()
    } catch (error) {
      console.error('Erro ao carregar orçamentos:', error)
    }
  }

  /**
   * Ordena a lista de orçamentos por critérios em cascata
   * 
   * A ordenação segue a seguinte prioridade:
   * 1. Valor gasto decrescente (quem gastou mais aparece primeiro)
   * 2. Valor orçado decrescente (em caso de empate no gasto)
   * 3. Nome da categoria em ordem alfabética (em caso de empate nos valores)
   * 
   * Trata defensivamente valores null/undefined para evitar erros em dados incompletos.
   * 
   * @param {Array} budgets - Lista de orçamentos a ser ordenada
   * @returns {Array} Nova lista ordenada (não modifica o array original)
   * @private
   */
  _sortBudgets(budgets) {
    return [...budgets].sort((a, b) => {
      // 1. Por valor gasto (decrescente) — quem gastou mais aparece primeiro
      const gastoCompare = (b.valorGasto || 0) - (a.valorGasto || 0)
      if (gastoCompare !== 0) return gastoCompare
      // 2. Por valor orçado (decrescente) — desempate pelo maior orçamento
      const orcadoCompare = (b.valorOrcado || b.valorLimite || 0) - (a.valorOrcado || a.valorLimite || 0)
      if (orcadoCompare !== 0) return orcadoCompare
      // 3. Por nome da categoria (alfabético crescente)
      return (a.categoria?.nome || '').localeCompare(b.categoria?.nome || '')
    })
  }

  async _handleBudgetSave(e) {
    const { id, budget: newBudget } = e.detail
    
    try {
      await orcamentoCacheService.atualizar(id, { valorLimite: newBudget, valorOrcado: newBudget })
      await this._loadOrcamentos()
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error)
    }
  }

  connectedCallback() {
    super.connectedCallback()
    // Garantir que o período atual esteja selecionado antes de carregar
    this.selectedPeriod = this._getCurrentPeriod()
    // Registrar listener global para fechar dropdown ao clicar fora
    document.addEventListener('click', this._handleDocumentClick)

    this._loadWithAuthGuard(async () => {
      await this._loadOrcamentos()
    })
  }

  /**
   * Remove listeners globais ao desconectar o componente
   * 
   * Evita memory leaks removendo os listeners de clique no documento,
   * de cache de orçamentos e de cache de categorias que controlam
   * a reatividade automática da listagem.
   */
  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this._handleDocumentClick)
    window.removeEventListener('orcamentos-cache-updated', this._handleOrcamentosCacheUpdated)
    window.removeEventListener('categorias-cache-updated', this._handleCategoriasCacheUpdated)
  }

  render() {
    return html`
      <premium-guard>
        <section class="section">
        <div class="container" style="padding-bottom: 90px;">
          <!-- Título da página -->
          <div class="mb-4">
            <h1 class="title is-4 mb-0" data-cy="orcamento-title">Orçamento</h1>
          </div>

          <!-- Linha de controles: seletor de período à esquerda + botão adicionar à direita -->
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
            <seletor-periodo
              .mes="${this._mesSeletor}"
              .ano="${this._anoSeletor}"
              @periodo-changed="${this._onPeriodoChanged}"
            ></seletor-periodo>
            <button
              class="button is-primary is-small"
              data-cy="btn-novo-orcamento"
              @click="${this._createBudget}"
              style="margin-right: 0.5rem;"
            >
              <span class="icon is-small">
                <i class="fas fa-plus"></i>
              </span>
              <span>Adicionar</span>
            </button>
          </div>

          <div class="columns is-multiline is-centered">
            ${this.authError ? html`
              <div class="column is-12">
                <div class="notification is-danger is-light" data-cy="auth-error-message">
                  <span class="icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </span>
                  <span>${this.authError}</span>
                </div>
              </div>
            ` : ''}
            ${this.filteredBudgets.length === 0 ? html`
              <div class="column is-12">
                <div class="card" style="background-color: var(--bg-surface, #151C30); border-radius: var(--radius-card, 20px); border: 1px solid var(--border, #26304A);">
                  <div class="card-content has-text-centered py-6">
                    <span class="icon is-large" style="color: var(--text-disabled, #6E7A96);">
                      <i class="fas fa-chart-line fa-3x"></i>
                    </span>
                    <p class="mt-4 mb-2" style="color: var(--text-secondary, #A8B3CF);">Nenhum orçamento encontrado para ${this._formatPeriodDisplay(this.selectedPeriod)}</p>
                    <p class="is-size-7" style="color: var(--text-disabled, #6E7A96);">Configure orçamentos para suas categorias e acompanhe seus gastos mensais</p>
                  </div>
                </div>
              </div>
            ` : this._sortBudgets(this.filteredBudgets).map(budget => html`
              <div class="column is-4-tablet is-4-desktop">
                <div class="card" data-cy="budget-card" style="border-radius: var(--radius-card, 20px); background-color: var(--bg-surface, #151C30); border: 1px solid var(--border, #26304A); box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));">
                  <header class="card-header" style="border-bottom: 1px solid var(--border, #26304A); border-radius: var(--radius-card, 20px) var(--radius-card, 20px) 0 0; box-shadow: none;">
                    <div class="is-flex is-align-items-center is-justify-content-space-between" style="width: 100%; padding: 0.75rem 1rem;">
                      <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                        <span class="is-size-5" data-cy="budget-emoji">${budget.categoria?.emoji || '💰'}</span>
                        <p class="card-header-title" data-cy="budget-category" style="padding: 0; color: var(--text-primary, #F4F7FF);">${budget.categoria?.nome || 'Categoria'}</p>
                      </div>
                      <!-- Menu dropdown com ações Editar e Excluir -->
                      <div class="dropdown is-right ${this._openMenuId === budget.id ? 'is-active' : ''}">
                        <div class="dropdown-trigger">
                          <button
                            class="button is-small is-ghost"
                            data-cy="btn-menu-budget"
                            aria-haspopup="true"
                            aria-controls="dropdown-menu-${budget.id}"
                            aria-label="Menu de ações do orçamento"
                            @click="${(e) => this._toggleMenu(budget.id, e)}"
                            style="color: var(--text-secondary, #A8B3CF); border: none; background: transparent;"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-ellipsis-v"></i>
                            </span>
                          </button>
                        </div>
                        <div class="dropdown-menu" id="dropdown-menu-${budget.id}" role="menu" style="min-width: 140px;">
                          <div class="dropdown-content" style="background-color: var(--bg-surface-secondary, #1D2740); border: 1px solid var(--border, #26304A); border-radius: var(--radius-input, 12px); box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));">
                            <a
                              class="dropdown-item"
                              data-cy="btn-edit-budget"
                              @click="${() => this._editBudget(budget.id)}"
                              style="color: var(--text-primary, #F4F7FF);"
                            >
                              <span class="icon is-small" style="margin-right: 0.5rem;">
                                <i class="fas fa-edit"></i>
                              </span>
                              Editar
                            </a>
                            <a
                              class="dropdown-item"
                              data-cy="btn-delete-budget"
                              @click="${() => this._confirmDelete(budget)}"
                              style="color: var(--danger, #EF4444);"
                            >
                              <span class="icon is-small" style="margin-right: 0.5rem;">
                                <i class="fas fa-trash-alt"></i>
                              </span>
                              Excluir
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </header>
                  <div class="card-content" style="padding: 1rem;">
                    ${this._renderCardContent(budget)}
                  </div>
                </div>
              </div>
            `)}
          </div>
        </div>

        <modal-orcamento @budget-save="${this._handleBudgetSave}"></modal-orcamento>

        <!-- Modal de confirmação de exclusão de orçamento -->
        ${this._budgetToDelete ? html`
          <div class="modal is-active" data-cy="modal-confirm-delete-budget">
            <div class="modal-background" @click="${this._cancelDelete}"></div>
            <div class="modal-card" style="max-width: 420px; border-radius: var(--radius-card, 20px); overflow: hidden;">
              <header class="modal-card-head" style="background-color: var(--bg-surface-secondary, #1D2740); border-bottom: 1px solid var(--border, #26304A);">
                <p class="modal-card-title" style="color: var(--text-primary, #F4F7FF); font-size: 1.1rem;">Confirmar exclusão</p>
                <button class="delete" aria-label="Fechar" @click="${this._cancelDelete}"></button>
              </header>
              <section class="modal-card-body" style="background-color: var(--bg-surface, #151C30);">
                <p style="color: var(--text-primary, #F4F7FF);">
                  Tem certeza que deseja excluir o orçamento de <strong style="color: var(--text-primary, #F4F7FF);">${this._budgetToDelete.categoria?.nome || 'Categoria'}</strong>?
                </p>
                ${this._deleteError ? html`
                  <div class="notification is-danger is-light mt-3" data-cy="delete-error-message" style="background-color: var(--bg-button-danger-light, rgba(220, 53, 69, 0.2)); color: var(--text-button-danger-light, #EF4444); border: none;">
                    <span class="icon is-small"><i class="fas fa-exclamation-circle"></i></span>
                    <span>${this._deleteError}</span>
                  </div>
                ` : ''}
              </section>
              <footer class="modal-card-foot is-flex is-justify-content-flex-end" style="background-color: var(--bg-surface-secondary, #1D2740); border-top: 1px solid var(--border, #26304A); gap: 0.5rem;">
                <button
                  class="button"
                  data-cy="btn-cancel-delete-budget"
                  @click="${this._cancelDelete}"
                  style="border-radius: var(--radius-button, 14px); background-color: var(--bg-surface, #151C30); color: var(--text-primary, #F4F7FF); border: 1px solid var(--border, #26304A);"
                >
                  Cancelar
                </button>
                <button
                  class="button"
                  data-cy="btn-confirm-delete-budget"
                  @click="${this._executeDelete}"
                  style="border-radius: var(--radius-button, 14px); background-color: var(--danger, #EF4444); color: white; border: none;"
                >
                  Excluir
                </button>
              </footer>
            </div>
          </div>
        ` : ''}

        </section>
      </premium-guard>
    `
  }
}

customElements.define('orcamentos-page', Orcamento)